import { uiText } from '../i18n/uiText';
import React, { useEffect, useRef, useState } from 'react';
import {useAuth} from '../store/AuthContext';
import useDraftBuffer from '../hooks/useDraftBuffer';
import DraftRecoveryNotice from '../components/common/DraftRecoveryNotice';
import SaveStatus from '../components/common/SaveStatus';
import FormField, {TextInput, TextArea} from '../components/common/FormField';
import { FileText, Send } from 'lucide-react';
import api, { auditReportsApi, auditsApi, getApiErrorMessage, outgoingEmailsApi } from '../api';
import Dialog from '../components/common/Dialog';
import { LoadingState } from '../components/common/UI';
import PageIntro, { HelpText } from '../components/ui/PageIntro';

const EMPTY_FORM = {
  audit_id: '',
  report_number: '',
  audit_number: '',
  basis: '',
  enterprise_name: '',
  enterprise_address: '',
  site_address: '',
  executive_name: '',
  audit_scope: '',
  conclusion: '',
  supplier_qualification: '',
  recommendations: '',
  key_risks: '',
  sign_auditor: '',
  sign_company: '',
  approved_by: '',
};

function TextField({ label, value, onChange, required = false }) {
  return (
    <FormField label={label} required={required}><TextInput value={value} onChange={(event) => onChange(event.target.value)} required={required}/></FormField>
  );
}

function TextAreaField({ label, value, onChange, rows = 3 }) {
  return (
    <FormField label={label}><TextArea rows={rows} value={value} onChange={(event) => onChange(event.target.value)}/></FormField>
  );
}

export default function AuditReportsPage() {
  const {user}=useAuth();
  const buffer=useDraftBuffer(user?.id,'editor','audit-report');
  const busy=useRef(false);
  const [savedAt,setSavedAt]=useState(null);
  const [audits, setAudits] = useState([]);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [delivery, setDelivery] = useState(null), [recipients, setRecipients] = useState([]), [recipient, setRecipient] = useState(''), [deliveryError, setDeliveryError] = useState(''), [sending, setSending] = useState(false);
  const sendFlight = useRef(false);
  const openDelivery = async report => {
    setDelivery(report); setRecipients([]); setRecipient(''); setDeliveryError(''); setSending(true);
    try { const response = await api.get(`/outgoing-emails/report/${report.id}/recipients`); setRecipients(response.data); setRecipient(response.data[0]?.target || ''); }
    catch (e) { setDeliveryError(getApiErrorMessage(e)); } finally { setSending(false); }
  };
  const form=buffer.values.form || EMPTY_FORM;
  const setForm=value=>buffer.edit('form',typeof value==='function'?value(buffer.values.form || EMPTY_FORM):value);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const load = async () => {
    setError('');
    const [auditsResult, reportsResult] = await Promise.allSettled([
      auditsApi.list(),
      auditReportsApi.list(),
    ]);
    const messages = [];

    if (auditsResult.status === 'fulfilled') {
      setAudits(Array.isArray(auditsResult.value.data) ? auditsResult.value.data : []);
    } else {
      setAudits([]);
      messages.push(uiText("Аудиты: {v0}", {v0: (getApiErrorMessage(auditsResult.reason))}));
    }

    if (reportsResult.status === 'fulfilled') {
      setReports(Array.isArray(reportsResult.value.data) ? reportsResult.value.data : []);
    } else {
      setReports([]);
      messages.push(uiText("Отчёты: {v0}", {v0: (getApiErrorMessage(reportsResult.reason))}));
    }

    if (messages.length) setError(messages.join(' · '));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (busy.current || buffer.recovery) return;
    busy.current=true;
    const snapshot=buffer.snapshot();
    setSaving(true);
    setError('');
    try {
      await auditReportsApi.upsert({
        ...form,
        audit_id: Number(form.audit_id),
        conformity_summary: [],
        findings_summary: [],
        attachments: [],
      });
      buffer.acknowledge(snapshot,{committed:true,values:{form:{...form,report_number:''}}});
      setSavedAt(Date.now());
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      busy.current=false;
      setSaving(false);
    }
  };

  const queue = async (id, target) => {
    if (sendFlight.current) return;
    sendFlight.current = true; setSending(true); setDeliveryError('');
    try {
      await outgoingEmailsApi.queueReport(id, target);
      setDelivery(null);
      alert(target === 'object'
        ? uiText("Отчёт поставлен в очередь на отправку объекту.")
        : uiText("Отчёт поставлен в очередь на отправку заказчику."));
    } catch (requestError) {
      setDeliveryError(getApiErrorMessage(requestError));
    } finally { sendFlight.current = false; setSending(false); }
  };

  if (loading) return <LoadingState message={uiText("Загружаем отчёты аудита…")} />;

  return (
    <div className="workspace-page audit-reports-page">
      <PageIntro
        icon={FileText}
        title={uiText("Отчёты по аудитам")}
        description={uiText("Итоговый документ с областью проверки, выводами, рисками, рекомендациями и реквизитами согласования.")}
      >
        <HelpText>{uiText("Выберите аудит, заполните итоговые выводы и сохраните отчёт. После сохранения его можно направить объекту или заказчику.")}</HelpText>
      </PageIntro>

      {error ? <div className="alert alert-error">{error}</div> : null}
      <DraftRecoveryNotice buffer={buffer} label={uiText("отчёт аудита — проверьте выбранный аудит")} disabled={saving}/>
      <SaveStatus saving={saving} error={error} dirty={buffer.count>0} recovery={buffer.recovery} savedAt={savedAt}/>

      <form className="card audit-report-form" onSubmit={submit}>
        <div className="card-header">
          <div>
            <div className="card-title">{uiText("Сформировать или обновить отчёт")}</div>
            <div className="workspace-muted">{uiText("Реквизиты и стороны")}</div>
          </div>
        </div>
        <div className="card-body document-form-body">
          <fieldset className="ap-form-lock" disabled={saving || !!buffer.recovery}>
          <section className="document-form-section" aria-labelledby="audit-report-details">
            <div className="document-form-section-heading" id="audit-report-details">{uiText("Основные данные")}</div>
            <div className="form-row form-row-three">
              <div className="form-group">
                <label className="form-label required">{uiText("Аудит")}</label>
                <select className="form-control" value={form.audit_id} onChange={(event) => updateField('audit_id', event.target.value)} required>
                  <option value="">{uiText("Выберите аудит")}</option>
                  {audits.map((audit) => <option key={audit.id} value={audit.id}>{audit.title}</option>)}
                </select>
              </div>
              <TextField label={uiText("Номер отчёта")} value={form.report_number} onChange={(value) => updateField('report_number', value)} required />
              <TextField label={uiText("Номер аудита")} value={form.audit_number} onChange={(value) => updateField('audit_number', value)} />
            </div>
            <div className="form-row">
              <TextField label={uiText("Основание")} value={form.basis} onChange={(value) => updateField('basis', value)} />
              <TextField label={uiText("Предприятие")} value={form.enterprise_name} onChange={(value) => updateField('enterprise_name', value)} />
            </div>
            <div className="form-row">
              <TextField label={uiText("Адрес предприятия")} value={form.enterprise_address} onChange={(value) => updateField('enterprise_address', value)} />
              <TextField label={uiText("Адрес площадки")} value={form.site_address} onChange={(value) => updateField('site_address', value)} />
            </div>
            <div className="form-row">
              <TextField label={uiText("Руководитель")} value={form.executive_name} onChange={(value) => updateField('executive_name', value)} />
              <TextField label={uiText("Квалификация поставщика")} value={form.supplier_qualification} onChange={(value) => updateField('supplier_qualification', value)} />
            </div>
          </section>

          <section className="document-form-section" aria-labelledby="audit-report-conclusions">
            <div className="document-form-section-heading" id="audit-report-conclusions">{uiText("Результаты и выводы")}</div>
            <TextAreaField label={uiText("Область аудита")} value={form.audit_scope} onChange={(value) => updateField('audit_scope', value)} />
            <div className="form-row">
              <TextAreaField label={uiText("Заключение")} rows={4} value={form.conclusion} onChange={(value) => updateField('conclusion', value)} />
              <TextAreaField label={uiText("Ключевые риски")} rows={4} value={form.key_risks} onChange={(value) => updateField('key_risks', value)} />
            </div>
            <TextAreaField label={uiText("Рекомендации")} value={form.recommendations} onChange={(value) => updateField('recommendations', value)} />
          </section>

          <section className="document-form-section" aria-labelledby="audit-report-approval">
            <div className="document-form-section-heading" id="audit-report-approval">{uiText("Подписи и утверждение")}</div>
            <div className="form-row form-row-three">
              <TextField label={uiText("Подпись аудитора")} value={form.sign_auditor} onChange={(value) => updateField('sign_auditor', value)} />
              <TextField label={uiText("Подпись предприятия")} value={form.sign_company} onChange={(value) => updateField('sign_company', value)} />
            <TextField label={uiText("Утверждено")} value={form.approved_by} onChange={(value) => updateField('approved_by', value)} />
            </div>
          </section>
          </fieldset>
        </div>
        <div className="card-footer document-form-footer">
          <button className="btn btn-primary" type="submit" disabled={saving || !!buffer.recovery}>
            {saving ? uiText("Сохраняем…") : uiText("Сохранить отчёт")}
          </button>
        </div>
      </form>

      <section className="card audit-report-registry" aria-labelledby="saved-audit-reports">
        <div className="card-header">
          <div>
            <div className="card-title" id="saved-audit-reports">{uiText("Сохранённые отчёты")}</div>
            <div className="workspace-muted">{reports.length ? `${reports.length} ${reports.length === 1 ? uiText("документ") : uiText("документов")}` : uiText("Документов пока нет")}</div>
          </div>
        </div>
        <div className="card-body">
          {reports.length ? (
            <div className="audit-report-list">
              {reports.map((item) => (
                <article className="audit-report-list-item" key={item.id}>
                  <div className="audit-report-list-copy">
                    <div className="audit-report-list-title">{item.report_number || uiText("Отчёт #{v0}", {v0: (item.id)})}</div>
                    <div className="workspace-muted">{uiText("Аудит #")}{item.audit_id} · {item.enterprise_name || uiText("Предприятие не указано")} · {item.site_address || uiText("Площадка не указана")}</div>
                    <p>{item.conclusion || uiText("Заключение пока не заполнено.")}</p>
                  </div>
                  <div className="row-primary-action">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => openDelivery(item)}><Send size={14} />{uiText('Отправить отчёт')}</button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="workspace-empty-state compact">
              <div className="workspace-empty-state-title">{uiText("Сохранённых отчётов пока нет")}</div>
              <div className="workspace-empty-state-text">{uiText("Выберите аудит и заполните форму выше — новый документ появится в этом реестре.")}</div>
            </div>
          )}
        </div>
      </section>
      {delivery && <Dialog label={uiText('Отправка отчёта')} onClose={() => setDelivery(null)} closeDisabled={sending}><div className="ap-dialog__header"><h2 className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText('Отправка отчёта')}</h2></div><div className="ap-dialog__body"><p>{delivery.report_number}</p>{deliveryError && <p role="alert" className="alert alert-error">{deliveryError}</p>}{recipients.length ? <FormField label={uiText('Получатель')}><select className="ap-control" value={recipient} onChange={e => setRecipient(e.target.value)} disabled={sending}>{recipients.map(r => <option key={r.target} value={r.target}>{r.label} · {r.email}</option>)}</select></FormField> : <p>{sending ? uiText('Проверяем получателей…') : uiText('Нет доступного адреса. Добавьте представителя организации с электронной почтой.')}</p>}</div><div className="ap-dialog__footer"><button className="ap-button ap-button--secondary" disabled={sending} onClick={() => setDelivery(null)}>{uiText('Отмена')}</button><button className="ap-button ap-button--primary" disabled={sending || !recipient} onClick={() => queue(delivery.id, recipient)}>{sending ? uiText('Подождите…') : uiText('Отправить')}</button></div></Dialog>}
    </div>
  );
}
